import { ApiResult } from 'src/Libs/api'
import { handleFetchError } from 'src/Libs/api/utils'
import { ShopAuthFetchWithResponse } from '../types'
import {
  CrossSellingProductsResponse,
  ShopBannerResponse,
  ShopHomePageResponse,
  OrderHistoryResponse,
  OrderHistoryDetailResponse,
  CancelOrderShopResponse,
  SearchResponseModel,
  ShopAdditionalProductsModelResponse,
} from '../models'
import { StatusCodes } from '@e3dc-react/shell/Libs/http-status-codes'
import { SelectedFilter, ShopProductCategoryResponse } from 'src/Libs/shop/models/shop-product-category.model'
import { ProductResponse } from '../models/product.model'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { TOTAL_PRODUCTS_PER_PAGE } from '../consts'

/*
 * This endpoint will return all Navigation Categories from the Shop
 */

export const fetchHomePage = async (shopFetch: ShopAuthFetchWithResponse): Promise<ApiResult<ShopHomePageResponse[]>> => {
  const payload = {
    activeId: 'main-navigation',
    rootId: 'main-navigation',
    associations: {
      media: {},
    },
    includes: {
      category: ['id', 'name', 'description', 'media', 'externalLink', 'translated', 'type'],
      media: ['url', 'thumbnails'],
      media_thumbnail: ['width', 'height', 'url'],
    },
  }

  try {
    const response = await shopFetch<ShopHomePageResponse[]>({
      path: 'navigation/main-navigation/main-navigation',
      method: 'GET',
      criteria: payload,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<ShopHomePageResponse[]>(response.data, true)
    }

    return new ApiResult<ShopHomePageResponse[]>([], false)
  } catch (error) {
    return new ApiResult<ShopHomePageResponse[]>([], true)
  }
}

/*
 * This Endpoint will Return all Url's for Navigation on the Side Bar Navigation
 */

export const fetchShopLinks = async (shopFetch: ShopAuthFetchWithResponse): Promise<ApiResult<ShopHomePageResponse[]>> => {
  const payload = {
    activeId: 'main-navigation',
    rootId: 'main-navigation',
    associations: {
      media: {},
    },
    includes: {
      category: ['id', 'name', 'externalLink', 'translated', 'type'],
    },
  }

  try {
    const response = await shopFetch<ShopHomePageResponse[]>({
      path: 'navigation/main-navigation/main-navigation',
      method: 'GET',
      criteria: payload,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<ShopHomePageResponse[]>(response.data, true)
    }

    return new ApiResult<ShopHomePageResponse[]>([], false)
  } catch (error) {
    return new ApiResult<ShopHomePageResponse[]>([], true)
  }
}

/*
 * This endpoint will get all the Products for an existing Category.
 */

export const fetchShopCategory = async (
  categoryId: string,
  page: number,
  shopFetch: ShopAuthFetchWithResponse,
  sorting?: string,
  limit?: number,
  filters?: SelectedFilter[]
): Promise<ApiResult<ShopProductCategoryResponse>> => {
  const query = {
    p: page,
    ...(sorting ? { order: sorting } : {}),
    ...(filters ? Object.assign({}, ...filters) : {}),
  }
  const payload = {
    includes: {
      product_listing: ['total', 'elements', 'aggregations', 'currentFilters', 'page', 'availableSortings', 'sorting'],
      product_sorting: ['translated', 'priority', 'key', 'label'],
      extensions: ['customFields'],
      product_media: ['media'],
      calculated_price: ['calculatedPrice', 'listPrice', 'unitPrice'],
      cart_list_price: ['price', 'discount'],
      media: ['url', 'thumbnails'],
      media_thumbnail: ['width', 'height', 'url'],
    },
    sort: [{ field: 'markAsTopseller', order: 'DESC' }],
    limit: limit ?? TOTAL_PRODUCTS_PER_PAGE,
  }

  try {
    const response = await shopFetch<ShopProductCategoryResponse>({
      path: `product-listing/${categoryId}`,
      method: 'GET',
      criteria: payload,
      query,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<ShopProductCategoryResponse>(response.data)
    }

    return new ApiResult<ShopProductCategoryResponse>(null)
  } catch (error) {
    return new ApiResult<ShopProductCategoryResponse>(null)
  }
}

/*
 * This endpoint will get all the infromation for a Product
 */

export const fetchSingleProduct = async (productId: string, shopFetch: ShopAuthFetchWithResponse): Promise<ApiResult<ProductResponse>> => {
  const payload = {
    associations: {
      manufacturer: {},
      media: {
        associations: {
          media: {
            associations: {
              thumbnails: {},
            },
          },
        },
      },
      crossSellings: {},
      properties: {
        associations: {
          group: {},
        },
      },
    },
    includes: {
      product: [
        'id',
        'productNumber',
        'e3dcPriceApplied',
        'calculatedPrice',
        'media',
        'translated',
        'manufacturer',
        'properties',
        'weight',
        'width',
        'height',
        'length',
        'name',
        'description',
        'cover',
        'crossSellings',
        'e3dcData',
        'e3dcAvailability',
        'customFields',
        'downloads',
      ],
      product_manufacturer: ['translated', 'name'],
      property_group: ['name', 'translated'],
      property_group_option: ['name', 'translated', 'group'],
      product_cross_selling: ['name', 'translated'],
    },
  }

  try {
    const response = await shopFetch<ProductResponse>({
      path: `product/${productId}`,
      method: 'GET',
      criteria: payload,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<ProductResponse>(response.data)
    }

    return new ApiResult<ProductResponse>(null)
  } catch (error) {
    return new ApiResult<ProductResponse>(null)
  }
}

/*
 * This endpoint will get all the similar products from an product
 */
export const fetchCrossSellingProducts = async (
  productId: string,
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<CrossSellingProductsResponse[]>> => {
  const payload = {
    includes: {
      product_listing: ['total', 'elements'],
      product: ['id', 'e3dcPriceApplied', 'available', 'name', 'cover', 'translated', 'calculatedPrice', 'extensions', 'e3dcData', 'markAsTopseller'],
      product_media: ['media'],
      calculated_price: ['calculatedPrice', 'listPrice', 'unitPrice'],
      cart_list_price: ['price', 'discount'],
      media: ['url', 'thumbnails'],
      media_thumbnail: ['width', 'height', 'url'],
    },
  }

  try {
    const response = await shopFetch<CrossSellingProductsResponse[]>({
      path: `product/${productId}/cross-selling`,
      method: 'GET',
      criteria: payload,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<CrossSellingProductsResponse[]>(response.data)
    }

    return new ApiResult<CrossSellingProductsResponse[]>(null)
  } catch (error) {
    return new ApiResult<CrossSellingProductsResponse[]>(null)
  }
}

/*
 * This endpoint will return all shop banners
 */
export const fetchShopBanners = async (shopFetch: ShopAuthFetchWithResponse): Promise<ApiResult<ShopBannerResponse>> => {
  const payload = {
    associations: {
      image: {},
    },
    includes: {
      e3dc_connector_banner: ['position', 'text', 'link', 'image', 'translated'],
      media: ['url', 'thumbnails'],
      media_thumbnail: ['width', 'height', 'url'],
    },
  }

  try {
    const response = await shopFetch<ShopBannerResponse>({
      path: 'e3dc/banner',
      method: 'GET',
      criteria: payload,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<ShopBannerResponse>(response.data)
    }

    return new ApiResult<ShopBannerResponse>(null)
  } catch (error) {
    return handleFetchError<ShopBannerResponse>(error)
  }
}

/*
 * This endpoint will get history data of orders
 */
export const fetchOrderHistory = async (
  page: number,
  limit: number,
  filterStatus: string[],
  filterSapOrderId: number | null,
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<OrderHistoryResponse>> => {
  const payload = {
    page,
    limit,
    filterStatus,
    filterSapOrderId,
  }

  try {
    const response = await shopFetch<OrderHistoryResponse>({
      path: `e3dc/order-history`,
      method: 'POST',
      payload: payload,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<OrderHistoryResponse>(response.data)
    }

    return new ApiResult<OrderHistoryResponse>(null, true)
  } catch (error) {
    return handleFetchError<OrderHistoryResponse>(error)
  }
}

/*
 * This endpoint will get history detailed data of an single order
 */
export const fetchOrderHistoryDetails = async (
  sapOrderId: number,
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<OrderHistoryDetailResponse>> => {
  try {
    const response = await shopFetch<OrderHistoryDetailResponse>({
      path: `e3dc/order-history/detail/${sapOrderId}`,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<OrderHistoryDetailResponse>(response.data)
    }

    return new ApiResult<OrderHistoryDetailResponse>(null, true)
  } catch (error) {
    return handleFetchError<OrderHistoryDetailResponse>(error)
  }
}

/*
 * This endpoint will cancel order from shop.
 */
export const cancelShopProductOrder = async (
  sapOrderId: number,
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<CancelOrderShopResponse>> => {
  try {
    const response = await shopFetch<CancelOrderShopResponse>({
      path: `e3dc/order/${sapOrderId}/cancel`,
      method: 'POST',
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<CancelOrderShopResponse>(response.data, false)
    }

    return new ApiResult<CancelOrderShopResponse>(null, true)
  } catch (error) {
    return handleFetchError<CancelOrderShopResponse>(error)
  }
}

/*
 * This endpoint will get the whole list of products based on specific search.
 */
export const getShopSearchAllProducts = async (
  search: string,
  page: number,
  shopFetch: ShopAuthFetchWithResponse,
  sorting?: string,
  filters?: SelectedFilter[]
): Promise<ApiResult<ShopProductCategoryResponse, AuthFetchError | Error>> => {
  try {
    const query = {
      search,
      p: page,
      ...(sorting ? { order: sorting } : {}),
      ...(filters ? Object.assign({}, ...filters) : {}),
    }
    const payload = {
      includes: {
        product_listing: ['total', 'elements', 'aggregations', 'currentFilters', 'page'],
        extensions: ['customFields'],
        product_media: ['media'],
        calculated_price: ['calculatedPrice', 'listPrice', 'unitPrice'],
        cart_list_price: ['price', 'discount'],
        media: ['url', 'thumbnails'],
        media_thumbnail: ['width', 'height', 'url'],
      },
    }

    const response = await shopFetch<ShopProductCategoryResponse>({
      path: `search`,
      method: 'GET',
      criteria: payload,
      query,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<ShopProductCategoryResponse, AuthFetchError | Error>(response.data, false)
    }

    return new ApiResult<ShopProductCategoryResponse, AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<ShopProductCategoryResponse>(error)
  }
}

/*
 * This endpoint will get the suggested products based on specific search.
 */
export const getShopSearchSuggestions = async (search: string, shopFetch: ShopAuthFetchWithResponse): Promise<ApiResult<SearchResponseModel>> => {
  try {
    const response = await shopFetch<SearchResponseModel>({
      path: 'search-suggest',
      method: 'GET',
      criteria: {
        includes: {
          product_listing: ['elements'],
          product: ['cover', 'id', 'translated', 'productNumber', 'name'],
        },
      },
      query: { search },
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<SearchResponseModel>(response.data, false)
    }

    return new ApiResult<SearchResponseModel>(null, true)
  } catch (error) {
    return handleFetchError<SearchResponseModel>(error)
  }
}

/*
 * This endpoint will get all additional products for a specified product
 */
export const fetchShopAddditionalProducts = async (
  mainProductId: string,
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<ShopAdditionalProductsModelResponse>> => {
  try {
    const payload = {
      includes: {
        e3dc_connector_product_data: ['additionalRequiredProducts'],
        e3dc_connector_product_data_required_product: ['product', 'productId', 'position'],
        product: [
          'calculatedPrice',
          'cover',
          'e3dcData',
          'e3dcAvailability',
          'id',
          'media',
          'name',
          'description',
          'productNumber',
          'translated',
          'width',
          'weight',
          'height',
          'length',
          'e3dcPriceApplied',
          'minPurchase',
          'downloads',
        ],
      },
      associations: {
        e3dcData: {
          associations: {
            additionalRequiredProducts: {
              associations: {
                product: {
                  associations: {
                    media: {
                      associations: {
                        media: {},
                      },
                    },
                    cover: {
                      associations: {
                        media: {
                          associations: {
                            thumbnails: {},
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
    }
    const response = await shopFetch<ShopAdditionalProductsModelResponse>({
      path: `e3dc/additional-required-products/${mainProductId}`,
      method: 'POST',
      payload: payload,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<ShopAdditionalProductsModelResponse>(response.data, true)
    }

    return new ApiResult<ShopAdditionalProductsModelResponse>(null)
  } catch (error) {
    return new ApiResult<ShopAdditionalProductsModelResponse>(null)
  }
}
